import React, { useCallback, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box, Button } from "@mui/material";
import color from "../../Constants/colors";
import { UserRole } from "../../Enums/companyStatus";
import { hasRole } from "../../Helpers/authHelpers";
import {
  getDocumentCraftingWorkspaceId,
  setDocumentCraftingWorkspaceId,
} from "../../Helpers/documentCraftingWorkspace";
import { isDocumentCraftingQuery } from "../../Query/useDocumentCrafting";
import { useWorkspace } from "../../Query/useWorkspace";
import DocumentCraftingThemeProvider from "./Common/DocumentCraftingThemeProvider";
import { DocumentCraftingWorkspaceContext } from "./Common/DocumentCraftingWorkspaceContext";

// Shown while a platform admin works in another workspace than their own.
const AdminWorkspaceBanner: React.FC<{
  workspaceId: number;
  onReset: () => void;
}> = ({ workspaceId, onReset }) => {
  const { t } = useTranslation("documentCrafting");
  const workspace = useWorkspace(String(workspaceId));
  const name = workspace.data?.workspace.name ?? `#${workspaceId}`;
  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={onReset}>
          {t("module.adminWorkspace.reset")}
        </Button>
      }
    >
      {t("module.adminWorkspace.banner", { name })}
    </Alert>
  );
};

/** Frame of every Document Crafting page: the light module theme and, for
 * platform admins, the workspace they act on. The module itself is one entry
 * of the main sidebar (navigationItems.tsx). */
const DocumentCraftingLayout: React.FC = () => {
  const queryClient = useQueryClient();
  const isAdmin = hasRole(UserRole.ADMIN);
  const [workspaceId, setWorkspaceId] = useState(() =>
    isAdmin ? getDocumentCraftingWorkspaceId() : null,
  );

  const switchWorkspace = useCallback(
    (next: number | null) => {
      if (!isAdmin) return;
      setDocumentCraftingWorkspaceId(next);
      queryClient.removeQueries({ predicate: isDocumentCraftingQuery });
      setWorkspaceId(next);
    },
    [isAdmin, queryClient],
  );

  const workspace = useMemo(
    () => ({ workspaceId, switchWorkspace }),
    [workspaceId, switchWorkspace],
  );

  return (
    <DocumentCraftingThemeProvider>
      <DocumentCraftingWorkspaceContext.Provider value={workspace}>
        <Box sx={{ minHeight: "100%", backgroundColor: color.gray50 }}>
          <Box
            sx={{
              maxWidth: 1440,
              mx: "auto",
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 3 },
            }}
          >
            {workspaceId !== null && (
              <AdminWorkspaceBanner
                workspaceId={workspaceId}
                onReset={() => switchWorkspace(null)}
              />
            )}
            {/* Pages remount with the workspace, so no state carries over. */}
            <Outlet key={workspaceId ?? "own"} />
          </Box>
        </Box>
      </DocumentCraftingWorkspaceContext.Provider>
    </DocumentCraftingThemeProvider>
  );
};

export default DocumentCraftingLayout;
